import { useState, useMemo, useCallback, useEffect, type ReactElement } from "react";
import { useLingui } from "@lingui/react";
import { Trans } from "@lingui/macro";
import {
  format,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  addDays,
  addWeeks,
  subWeeks,
  subDays,
  addMonths,
  subMonths,
} from "date-fns";
import { getDateFnsLocale } from "@/utils/localeUtils";

import { ChevronLeft, ChevronRight, Plus, Filter, X, Building2, Users, Send, Undo2, Copy, Lightbulb, UserRound, Printer, Download, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { HelpTip } from "@/components/ui/help-tip";
import { Skeleton } from "@/components/ui/skeleton";
import { QueryErrorState } from "@/components/ui/query-error-state";
import { formatDateLong, formatDateRange } from "@/lib/format";
import { DatePicker } from "@/components/ui/date-picker";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

import { useShifts, useShiftPattern } from "@/hooks/useShifts";
import { useShiftRequests } from "@/hooks/useShiftRequests";
import { OPEN_SHIFT_VALUE, ShiftRequestMarksProvider } from "@/components/shifts/openShifts";
import { useDownloadShiftPlanExport } from "@/hooks/useShiftExport";
import { useCoverage, useSlotCoverage } from "@/hooks/useShiftCoverage";
import { useZeitkontoProjection } from "@/hooks/useZeitkonto";
import { useAllEmployees } from "@/hooks/useEmployees";
import { useGroups } from "@/hooks/useGroups";
import { useGroupCombinations } from "@/hooks/useGroupCombinations";
import { useIsAdmin } from "@/hooks/useIsAdmin";
import { useShiftWriteScope } from "@/hooks/useShiftWriteScope";
import { canWriteShift } from "@/components/shifts/shiftWriteScope";
import type { CombinationSuggestion, GroupCombination } from "@/types/groupCombinations";
import { CombineGroupsDialog } from "@/components/shifts/CombineGroupsDialog";
import { CombinationSuggestionsPanel } from "@/components/shifts/CombinationSuggestionsPanel";
import { firstDateOnWeekday } from "@/components/shifts/combinationLayout";
import { parseDateString } from "@/utils/dateUtils";
import { useEmployeeAbsences } from "@/hooks/useEmployeeAbsences";
import { useChildcares } from "@/hooks/settings/useChildcare";
import type { Shift, CalendarViewMode } from "@/types/shifts";
import type { EmployeeAbsence } from "@/types/employeeAbsences";
import { ShiftDetailDialog } from "@/components/shifts/ShiftDetailDialog";
import { CreateShiftDialog } from "@/components/shifts/CreateShiftDialog";
import { EditShiftDialog } from "@/components/shifts/EditShiftDialog";
import { DeleteShiftDialog } from "@/components/shifts/DeleteShiftDialog";
import { DeleteSeriesDialog } from "@/components/shifts/DeleteSeriesDialog";
import { ShiftCalendarGrid } from "@/components/shifts/ShiftCalendarGrid";
import { EditAbsenceDialog } from "@/components/absences/EditAbsenceDialog";
import { PublishWeekDialog } from "@/components/shifts/PublishWeekDialog";
import { ShiftMonthView } from "@/components/shifts/ShiftMonthView";
import { CopyWeekDialog } from "@/components/shifts/CopyWeekDialog";
import { PlannerStatusStrip } from "@/components/shifts/PlannerStatusStrip";
import { useShiftMove } from "@/components/shifts/ShiftMoveDialogs";
import { useAvailabilityRange } from "@/hooks/useEmployeeAvailability";
import { PlannerRangePicker } from "@/components/shifts/PlannerRangePicker";
import { applyEmployeeFilter, employeesForFilter } from "@/components/shifts/plannerFilters";
import { EnhancedSingleSelect } from "@/components/ui/enhanced-singleselect";
import { type ViewMode } from "@/utils/shiftHelpers";
import { toast } from "sonner";

/**
 * A toolbar button with a one-line explanation (#2038 UX pass): a planner who opens the
 * page for the first time learns what Publish, Unpublish, Combine groups or Suggestions
 * do before clicking. The tooltip opens on hover and on keyboard focus.
 */
function ExplainedButton({
  tip,
  disabled = false,
  children,
}: {
  tip: string;
  /** A disabled button gets no pointer events; a focusable wrapper carries the tooltip (#2119). */
  disabled?: boolean;
  children: ReactElement;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        {disabled ? (
          <span tabIndex={0} className="inline-flex rounded-md" data-testid="explained-button-disabled">
            {children}
          </span>
        ) : (
          children
        )}
      </TooltipTrigger>
      <TooltipContent className="max-w-xs text-left font-normal">{tip}</TooltipContent>
    </Tooltip>
  );
}

// Types for employee and group data used by ShiftCalendarGrid
interface EmployeeData {
  id?: string;
  firstname?: string;
  lastname?: string;
  avatarUrl?: string;
}

interface GroupData {
  id?: string;
  name?: string;
  avatarUrl?: string;
  color?: string;
  childcare_id?: string;
  // #1156: the map builder below already writes it and `ShiftCalendarGrid`
  // sorts resources by it; it was simply missing from this local mirror.
  sort_order?: number;
}

export interface ShiftPlanningPageProps {
  selectedDate: Date;
  onDateChange: (date: Date) => void;
  selectedChildcareId: string | null;
  onChildcareChange: (id: string | null) => void;
  selectedGroupId: string | null;
  onGroupChange: (id: string | null) => void;
  /** Returns and clears a pending employee ID for pre-selection (from coverage tab navigation) */
  consumePendingEmployee?: () => string | null;
  /** Initial calendar view; set by the deep-link from a split alert item (#1594). Defaults to "week". */
  initialCalendarView?: CalendarViewMode;
}

export function ShiftPlanningPage({
  selectedDate: sharedDate,
  onDateChange: setSharedDate,
  selectedChildcareId,
  onChildcareChange: setSelectedChildcareId,
  selectedGroupId,
  onGroupChange: setSelectedGroupId,
  consumePendingEmployee,
  initialCalendarView,
}: ShiftPlanningPageProps) {
  const { i18n } = useLingui();
  const dateFnsLocale = getDateFnsLocale(i18n.locale);

  // Page state
  const [calendarView, setCalendarView] = useState<CalendarViewMode>(initialCalendarView ?? "week");
  const [viewMode, setViewMode] = useState<ViewMode>("byGroup");
  const [selectedShift, setSelectedShift] = useState<Shift | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);

  // CRUD dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteSeriesDialogOpen, setDeleteSeriesDialogOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string | undefined>(undefined);

  // Publish/Unpublish dialog state
  const [publishDialogOpen, setPublishDialogOpen] = useState(false);
  const [publishDialogMode, setPublishDialogMode] = useState<'publish' | 'unpublish'>('publish');

  // Copy week dialog state
  const [copyWeekDialogOpen, setCopyWeekDialogOpen] = useState(false);

  // Group combination dialog state (null = create)
  const [combineDialogOpen, setCombineDialogOpen] = useState(false);
  const [selectedCombination, setSelectedCombination] = useState<GroupCombination | null>(null);
  const isAdmin = useIsAdmin();
  // Combining groups works on group rows of a day or a week (#2119 F8).
  const canCombine = viewMode === "byGroup" && calendarView !== "month";
  const canSuggest = viewMode === "byGroup";
  // Suggestions panel (#1579 part 4). The previewed suggestion shows until the panel
  // closes; "Create" fills in the combination dialog.
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const [previewSuggestion, setPreviewSuggestion] = useState<CombinationSuggestion | null>(null);
  const [suggestionPrefill, setSuggestionPrefill] = useState<{
    suggestion: CombinationSuggestion;
    validFrom: Date;
  } | null>(null);

  // Absence edit dialog state
  const [editAbsenceDialogOpen, setEditAbsenceDialogOpen] = useState(false);
  const [selectedAbsence, setSelectedAbsence] = useState<EmployeeAbsence | null>(null);

  // Use shared date for calendar navigation
  const currentDate = sharedDate;
  const setCurrentDate = setSharedDate;

  // Consume pending employee from coverage tab navigation
  useEffect(() => {
    if (consumePendingEmployee) {
      const empId = consumePendingEmployee();
      if (empId) {
        setCalendarView("day");
        setViewMode("byEmployee");
        setSelectedEmployeeId(empId);
        setSelectedDate(currentDate);
        setCreateDialogOpen(true);
      }
    }
  }, [consumePendingEmployee, currentDate]);

  // Filter state (popover open is local)
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  // Employee filter (#2038): one employee's rows (By Employee) or blocks (By Group).
  const [filterEmployeeId, setFilterEmployeeId] = useState<string | null>(null);

  // Calculate week start for navigation display
  const weekStart = useMemo(
    () => startOfWeek(currentDate, { weekStartsOn: 1 }),
    [currentDate]
  );
  const weekEnd = useMemo(
    () => endOfWeek(currentDate, { weekStartsOn: 1 }),
    [currentDate]
  );

  // Month boundaries for month view
  const monthStart = useMemo(() => startOfMonth(currentDate), [currentDate]);
  const monthEnd = useMemo(() => endOfMonth(currentDate), [currentDate]);

  // Get date range for API query
  const { fromDate, toDate } = useMemo(() => {
    if (calendarView === "day") {
      return {
        fromDate: format(currentDate, "yyyy-MM-dd"),
        toDate: format(currentDate, "yyyy-MM-dd"),
      };
    }
    if (calendarView === "month") {
      return {
        fromDate: format(monthStart, "yyyy-MM-dd"),
        toDate: format(monthEnd, "yyyy-MM-dd"),
      };
    }
    return {
      fromDate: format(weekStart, "yyyy-MM-dd"),
      toDate: format(weekEnd, "yyyy-MM-dd"),
    };
  }, [currentDate, weekStart, weekEnd, monthStart, monthEnd, calendarView]);

  // Fetch shifts for the visible range (higher limit for month view)
  const shiftsLimit = calendarView === "month" ? 1000 : undefined;
  const {
    data: shiftsResponse,
    isLoading: isLoadingShifts,
    isError,
    error,
    refetch: refetchShifts,
  } = useShifts({
    fromDate,
    toDate,
    limit: shiftsLimit,
  });

  // Open claims and swaps of the visible range (#2040): marks on the blocks, decisions in the
  // detail dialog.
  const { data: shiftRequestsData } = useShiftRequests(fromDate, toDate);

  // Fetch absences for the visible range (higher limit for month view)
  const absencesLimit = calendarView === "month" ? 1000 : 500;
  const { data: absencesResponse, isLoading: isLoadingAbsences } = useEmployeeAbsences({
    fromDate,
    toDate,
    limit: absencesLimit,
  });

  // Fetch coverage data for byGroup view
  // For day view: use the current date
  // For week view: use the current date (shows coverage snapshot for navigation date)
  const coverageDate = useMemo(() => format(currentDate, "yyyy-MM-dd"), [currentDate]);
  const { data: coverageData } = useCoverage(coverageDate);
  // Slot coverage feeds the per-group staffing strip, shown in the byGroup day view only.
  const isGroupDayView = viewMode === "byGroup" && calendarView === "day";
  const { data: slotCoverageData } = useSlotCoverage(coverageDate, { enabled: isGroupDayView });
  // Group combinations of the visible range, shown in the byGroup week and day views.
  // `GET /group-combinations` admits `capacity` and `shifts-absences` (#1688), so a
  // group head sees the overlay; the combination writes stay admin-only here.
  const { data: combinationsData } = useGroupCombinations(fromDate, toDate, {
    enabled: viewMode === "byGroup" && calendarView !== "month",
  });
  const combinations = useMemo(() => combinationsData ?? [], [combinationsData]);
  // The edit dialog always shows the latest version of the selected combination.
  const editingCombination = useMemo(
    () =>
      selectedCombination
        ? combinations.find((c) => c.id === selectedCombination.id) ?? selectedCombination
        : null,
    [combinations, selectedCombination]
  );
  // D12: the day's saving is the sum of each unit's minimum per-slot saving.
  const staffSavedToday = useMemo(() => {
    const units = slotCoverageData?.units ?? [];
    return units.length === 0 ? null : units.reduce((sum, unit) => sum + unit.staff_saved, 0);
  }, [slotCoverageData?.units]);

  // Fetch full employees and groups data for avatars
  const { data: employeesData, isLoading: isLoadingEmployees } = useAllEmployees();
  // Note: Groups API has a max limit of 100 (backend validation)
  const { data: groupsData, isLoading: isLoadingGroups } = useGroups(100, 0);

  // Fetch childcares for filter dropdown
  const { data: childcaresData } = useChildcares({ limit: 100, offset: 0 });

  const childcares = useMemo(() => childcaresData?.data || [], [childcaresData?.data]);
  const allGroups = useMemo(() => groupsData || [], [groupsData]);

  // Filter groups based on selected childcare
  const filteredGroupsForDropdown = useMemo(() => {
    if (!selectedChildcareId) return allGroups;
    return allGroups.filter(
      (group) => group.childcare_id === selectedChildcareId
    );
  }, [allGroups, selectedChildcareId]);

  // Get selected childcare and group names for badges
  const selectedChildcareName = useMemo(() => {
    if (!selectedChildcareId) return null;
    const childcare = childcares.find((c) => c.id === selectedChildcareId);
    return childcare?.name || null;
  }, [childcares, selectedChildcareId]);

  const selectedGroupName = useMemo(() => {
    if (!selectedGroupId) return null;
    const group = allGroups.find((g) => g.id === selectedGroupId);
    return group?.name || null;
  }, [allGroups, selectedGroupId]);

  const hasActiveFilters = selectedChildcareId || selectedGroupId || filterEmployeeId;

  const handleClearAllFilters = useCallback(() => {
    setSelectedChildcareId(null);
    setSelectedGroupId(null);
    setFilterEmployeeId(null);
  }, [setSelectedChildcareId, setSelectedGroupId]);

  const handleChildcareChange = useCallback((value: string) => {
    if (value === '__all__') {
      setSelectedChildcareId(null);
      setSelectedGroupId(null);
    } else {
      setSelectedChildcareId(value);
      // Clear group selection if it doesn't match the new childcare
      if (selectedGroupId) {
        const group = allGroups.find((g) => g.id === selectedGroupId);
        if (group?.childcare_id !== value) {
          setSelectedGroupId(null);
        }
      }
    }
  }, [selectedGroupId, allGroups, setSelectedChildcareId, setSelectedGroupId]);

  const handleGroupChange = useCallback((value: string) => {
    if (value === '__all__') {
      setSelectedGroupId(null);
    } else {
      setSelectedGroupId(value);
    }
  }, [setSelectedGroupId]);

  const isLoading = isLoadingShifts || isLoadingEmployees || isLoadingGroups || isLoadingAbsences;

  // Fetch pattern data when delete series dialog is open
  const { data: patternData, isError: patternFetchError } = useShiftPattern(
    selectedShift?.pattern_id || "",
    { enabled: deleteSeriesDialogOpen && !!selectedShift?.pattern_id }
  );

  // Handle pattern fetch error
  useEffect(() => {
    if (deleteSeriesDialogOpen && patternFetchError) {
      toast.error(
        i18n._({
          id: "shifts.deleteSeriesPatternFetchError",
          message: "Failed to fetch pattern for deleting series.",
        })
      );
      setDeleteSeriesDialogOpen(false);
    }
  }, [deleteSeriesDialogOpen, patternFetchError, i18n]);

  // Create lookup maps for employees and groups
  const employeeMap = useMemo(() => {
    const map = new Map<string, EmployeeData>();
    const employees = employeesData || [];
    employees.forEach((emp) => {
      if (emp.id) {
        map.set(emp.id, {
          id: emp.id,
          firstname: emp.firstname,
          lastname: emp.lastname,
          avatarUrl: emp.avatarUrl,
        });
      }
    });
    return map;
  }, [employeesData]);

  const groupMap = useMemo(() => {
    const map = new Map<string, GroupData>();
    const groupsList = groupsData || [];
    groupsList.forEach((g) => {
      if (g.id) {
        map.set(g.id, {
          id: g.id,
          name: g.name,
          avatarUrl: g.avatarUrl,
          color: g.color,
          childcare_id: g.childcare_id,
          sort_order: g.sort_order,
        });
      }
    });
    return map;
  }, [groupsData]);

  // Zeitkonto projection for the visible window — only meaningful in
  // week/day view + byEmployee mode (mirrors the byGroup-only coverage fetch).
  const isZeitkontoEnabled =
    (calendarView === "week" || calendarView === "day") &&
    viewMode === "byEmployee";
  const zeitkontoEmployeeIds = useMemo(
    () => Array.from(employeeMap.keys()),
    [employeeMap]
  );
  const { byEmployee: zeitkontoByEmployee } = useZeitkontoProjection({
    fromDate,
    toDate,
    employeeIds: zeitkontoEmployeeIds,
    enabled: isZeitkontoEnabled,
  });

  // Memoize shifts array to prevent unnecessary re-renders
  const shifts = useMemo(
    () => shiftsResponse?.shifts || [],
    [shiftsResponse?.shifts]
  );

  // Memoize absences array to prevent unnecessary re-renders
  const absences = useMemo(
    () => absencesResponse?.absences || [],
    [absencesResponse?.absences]
  );

  // Employee filter options: active employees, of the selected childcare's groups when set.
  const employeeFilterOptions = useMemo(() => {
    const childcareGroupIds = selectedChildcareId
      ? allGroups.filter((g) => g.childcare_id === selectedChildcareId).map((g) => g.id ?? "")
      : null;
    return employeesForFilter(employeesData ?? [], childcareGroupIds)
      .filter((e) => !!e.id)
      .map((e) => ({ value: e.id as string, label: `${e.firstname ?? ""} ${e.lastname ?? ""}`.trim() }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [employeesData, allGroups, selectedChildcareId]);
  const filterEmployeeName = useMemo(
    () => employeeFilterOptions.find((o) => o.value === filterEmployeeId)?.label ?? null,
    [employeeFilterOptions, filterEmployeeId]
  );
  const { shifts: visibleShifts, employeeMap: visibleEmployeeMap } = useMemo(
    () => applyEmployeeFilter(shifts, employeeMap, filterEmployeeId),
    [shifts, employeeMap, filterEmployeeId]
  );
  const visibleAbsences = useMemo(
    () => (filterEmployeeId ? absences.filter((a) => a.employee_id === filterEmployeeId) : absences),
    [absences, filterEmployeeId]
  );

  // Navigation handlers
  const handlePrevious = useCallback(() => {
    if (calendarView === "day") {
      setCurrentDate(subDays(currentDate, 1));
    } else if (calendarView === "month") {
      setCurrentDate(subMonths(currentDate, 1));
    } else {
      setCurrentDate(subWeeks(currentDate, 1));
    }
  }, [calendarView, currentDate, setCurrentDate]);

  const handleNext = useCallback(() => {
    if (calendarView === "day") {
      setCurrentDate(addDays(currentDate, 1));
    } else if (calendarView === "month") {
      setCurrentDate(addMonths(currentDate, 1));
    } else {
      setCurrentDate(addWeeks(currentDate, 1));
    }
  }, [calendarView, currentDate, setCurrentDate]);

  const handleToday = useCallback(() => {
    setCurrentDate(new Date());
  }, [setCurrentDate]);

  const handleDateSelect = useCallback((date: Date | undefined) => {
    if (date) {
      setCurrentDate(date);
    }
  }, [setCurrentDate]);

  // Drag and drop (#2038): the drop asks series scope / published confirmation, then saves.
  // The drop's warning lines name the target employee (#2041).
  const employeeFirstName = useCallback(
    (id: string) => {
      const e = employeeMap.get(id);
      return e?.firstname?.trim() || e?.lastname?.trim() || "";
    },
    [employeeMap]
  );
  const shiftMove = useShiftMove(viewMode, employeeFirstName);

  // Availability hatch and blocked-day locks of the By Employee grid (#2041).
  const showsAvailability = viewMode === "byEmployee" && calendarView !== "month";
  const { data: availabilityRange } = useAvailabilityRange(fromDate, toDate, { enabled: showsAvailability });

  // Group-lead write scope (#2043): a lead changes only the shifts of the groups they lead;
  // every other shift is read-only here and refused by the server.
  const writeScope = useShiftWriteScope();
  const canEditShift = useCallback((shift: Shift) => canWriteShift(writeScope, shift), [writeScope]);
  const leadGroupIds = writeScope.unrestricted ? undefined : writeScope.groupIds;
  const leadGroupNames = useMemo(
    () => (leadGroupIds ?? []).map((id) => groupMap.get(id)?.name ?? id).join(", "),
    [leadGroupIds, groupMap]
  );

  // Print and CSV export of the visible range (#2038).
  const exportPlan = useDownloadShiftPlanExport();
  const handlePrint = useCallback(() => {
    window.print();
  }, []);
  const handleExportCsv = useCallback(() => {
    exportPlan.mutate({
      fromDate,
      toDate,
      childcareId: selectedChildcareId,
      groupId: selectedGroupId,
    });
  }, [exportPlan, fromDate, toDate, selectedChildcareId, selectedGroupId]);

  // The publish toast's action jumps back to the published range (#2038).
  const handleShowPublishedRange = useCallback((from: string) => {
    const day = parseDateString(from);
    if (day) setCurrentDate(day);
  }, [setCurrentDate]);

  // View change handler
  const handleViewChange = useCallback((view: CalendarViewMode) => {
    setCalendarView(view);
  }, []);

  // Shift click handler - opens detail dialog
  const handleShiftClick = useCallback((shift: Shift) => {
    setSelectedShift(shift);
    setDetailDialogOpen(true);
  }, []);

  // Cell click handler - opens create dialog
  const handleCellClick = useCallback((date: Date, resourceId: string) => {
    setSelectedDate(date);
    // The "Open shifts" row of the By Employee view pre-selects an open shift (#2040).
    const employeeId = resourceId === "unassigned" ? OPEN_SHIFT_VALUE : resourceId;
    setSelectedEmployeeId(viewMode === "byEmployee" || calendarView === "month" ? employeeId : undefined);
    setCreateDialogOpen(true);
  }, [viewMode, calendarView]);

  // Combination click handler - opens the combination dialog in edit mode
  const handleCombinationClick = useCallback((combination: GroupCombination) => {
    // The dialog is modal; the non-modal suggestions sheet closes first.
    setSuggestionsOpen(false);
    setPreviewSuggestion(null);
    setSuggestionPrefill(null);
    setSelectedCombination(combination);
    setCombineDialogOpen(true);
  }, []);

  // Suggestions panel handlers (#1579 part 4)
  const handleSuggestionsOpenChange = useCallback((open: boolean) => {
    setSuggestionsOpen(open);
    if (!open) setPreviewSuggestion(null);
  }, []);

  const handleCreateFromSuggestion = useCallback(
    (suggestion: CombinationSuggestion, from: string) => {
      setSuggestionsOpen(false);
      setPreviewSuggestion(null);
      setSelectedCombination(null);
      setSuggestionPrefill({ suggestion, validFrom: parseDateString(from) ?? currentDate });
      setCombineDialogOpen(true);
    },
    [currentDate]
  );

  const handleShowSuggestionOnDay = useCallback(
    (suggestion: CombinationSuggestion, from: string) => {
      setPreviewSuggestion(suggestion);
      setCalendarView("day");
      const start = parseDateString(from);
      if (start && suggestion.weekdays.length > 0) {
        setCurrentDate(firstDateOnWeekday(start, suggestion.weekdays[0]));
      }
    },
    [setCurrentDate]
  );

  // Absence click handler - opens edit absence dialog
  const handleAbsenceClick = useCallback((absence: EmployeeAbsence) => {
    setSelectedAbsence(absence);
    setEditAbsenceDialogOpen(true);
  }, []);

  // Edit handler - opens edit dialog from detail dialog
  const handleEditShift = useCallback((shift: Shift) => {
    setSelectedShift(shift);
    setDetailDialogOpen(false);
    setEditDialogOpen(true);
  }, []);

  // Delete handler - opens delete dialog from detail dialog
  const handleDeleteShift = useCallback((shift: Shift) => {
    setSelectedShift(shift);
    setDetailDialogOpen(false);
    setDeleteDialogOpen(true);
  }, []);

  // Delete series handler - opens delete series dialog from detail dialog
  const handleDeleteSeries = useCallback((shift: Shift) => {
    setSelectedShift(shift);
    setDetailDialogOpen(false);
    setDeleteSeriesDialogOpen(true);
  }, []);

  // Format navigation label based on view mode
  const getNavigationLabel = () => {
    if (calendarView === "day") {
      return formatDateLong(currentDate, i18n.locale);
    }
    if (calendarView === "month") {
      return format(currentDate, "LLLL yyyy", { locale: dateFnsLocale });
    }
    return formatDateRange(weekStart, weekEnd, i18n.locale, { style: "medium" });
  };

  return (
    <ShiftRequestMarksProvider requests={shiftRequestsData?.items}>
    <div className="flex flex-col shift-plan-print" data-testid="shift-plan">
      {/* Print header (#2038): the range and the filters, printed instead of the controls */}
      <div className="hidden print:block mb-3" data-testid="shift-plan-print-header">
        <h1 className="text-lg font-semibold">
          {i18n._({ id: "shifts.print.title", message: "Shift plan {range}", values: { range: getNavigationLabel() } })}
        </h1>
        {(selectedChildcareName || selectedGroupName || filterEmployeeName) && (
          <p className="text-sm">
            {[selectedChildcareName, selectedGroupName, filterEmployeeName].filter(Boolean).join(" · ")}
          </p>
        )}
      </div>

      {/* Action buttons row */}
      <TooltipProvider delayDuration={300}>
      <div className="pb-4 flex flex-wrap justify-end gap-2 print:hidden">
        <ExplainedButton
          tip={i18n._({
            id: "shifts.tip.print",
            message: "Prints the visible week or day on one landscape page, with the legend.",
          })}
        >
          <Button variant="outline" onClick={handlePrint} data-testid="shift-plan-print">
            <Printer className="mr-2 h-4 w-4" aria-hidden="true" />
            {i18n._({ id: "shifts.print.button", message: "Print" })}
          </Button>
        </ExplainedButton>
        <ExplainedButton
          tip={i18n._({
            id: "shifts.tip.export",
            message: "Downloads every shift of the visible period as a table for Excel, with the active childcare and group filter.",
          })}
        >
          <Button
            variant="outline"
            onClick={handleExportCsv}
            disabled={exportPlan.isPending}
            data-testid="shift-plan-export"
          >
            {exportPlan.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
            ) : (
              <Download className="mr-2 h-4 w-4" aria-hidden="true" />
            )}
            {i18n._({ id: "shifts.export.button", message: "Export (CSV)" })}
          </Button>
        </ExplainedButton>
        {calendarView === "week" && (
          <ExplainedButton
            tip={i18n._({
              id: "shifts.tip.copyWeek",
              message: "Copies the shifts of this week into one or more later weeks, as drafts.",
            })}
          >
            <Button
              variant="outline"
              onClick={() => setCopyWeekDialogOpen(true)}
            >
              <Copy className="mr-2 h-4 w-4" />
              {i18n._({ id: "shifts.copyWeek.button", message: "Copy Week" })}
            </Button>
          </ExplainedButton>
        )}
        <ExplainedButton
          tip={i18n._({
            id: "shifts.tip.unpublish",
            message: "Takes the published shifts of the visible period back to draft. Employees no longer see them in the app and are not notified.",
          })}
        >
          <Button
            variant="outline"
            onClick={() => {
              setPublishDialogMode('unpublish');
              setPublishDialogOpen(true);
            }}
          >
            <Undo2 className="mr-2 h-4 w-4" />
            {i18n._({ id: "shifts.unpublish.button", message: "Unpublish" })}
          </Button>
        </ExplainedButton>
        <ExplainedButton
          tip={i18n._({
            id: "shifts.tip.publish",
            message: "Makes the drafts of the visible period visible to the employees in the app. Each employee gets one notification.",
          })}
        >
          <Button
            variant="outline"
            onClick={() => {
              setPublishDialogMode('publish');
              setPublishDialogOpen(true);
            }}
          >
            <Send className="mr-2 h-4 w-4" />
            {i18n._({ id: "shifts.publish.button", message: "Publish" })}
          </Button>
        </ExplainedButton>
        {/* #2119 F8: admins see both buttons in every view, disabled with the reason where
            they cannot act; group leads read why in the planner help. */}
        {isAdmin && (
          <ExplainedButton
            disabled={!canCombine}
            tip={
              canCombine
                ? i18n._({
                    id: "shifts.tip.combine",
                    message: "Lets two or three groups share one room and their staff for a time window, for example early in the morning. The cover is then counted for the combined groups.",
                  })
                : i18n._({
                    id: "shifts.tip.combineGroupViewOnly",
                    message: "Only in the By Group view, by day or by week. Switch the view above the plan.",
                  })
            }
          >
            <Button
              variant="outline"
              disabled={!canCombine}
              data-testid="combine-groups-button"
              onClick={() => {
                setSuggestionsOpen(false);
                setPreviewSuggestion(null);
                setSuggestionPrefill(null);
                setSelectedCombination(null);
                setCombineDialogOpen(true);
              }}
            >
              <Users className="mr-2 h-4 w-4" />
              {i18n._({ id: "shifts.combination.button", message: "Combine groups" })}
            </Button>
          </ExplainedButton>
        )}
        {isAdmin && (
          <ExplainedButton
            disabled={!canSuggest}
            tip={
              canSuggest
                ? i18n._({
                    id: "shifts.tip.suggestions",
                    message: "Finds time windows in the next 4 weeks where combining groups needs fewer staff, based on the children's booked and usual times.",
                  })
                : i18n._({
                    id: "shifts.tip.suggestionsGroupViewOnly",
                    message: "Only in the By Group view. Switch the view above the plan.",
                  })
            }
          >
            <Button
              variant="outline"
              disabled={!canSuggest}
              onClick={() => setSuggestionsOpen(true)}
              data-testid="combination-suggestions-button"
            >
              <Lightbulb className="mr-2 h-4 w-4" aria-hidden="true" />
              {i18n._({ id: "shifts.suggestions.button", message: "Suggestions" })}
            </Button>
          </ExplainedButton>
        )}
        <ExplainedButton
          tip={i18n._({
            id: "shifts.tip.add",
            message: "Adds a shift as a draft. You can also click a free cell in the grid.",
          })}
        >
          <Button onClick={() => {
            setSelectedDate(null);
            setSelectedEmployeeId(undefined);
            setCreateDialogOpen(true);
          }}>
            <Plus className="mr-2 h-4 w-4" />
            {i18n._({ id: "common.add", message: "Add" })}
          </Button>
        </ExplainedButton>
      </div>
      </TooltipProvider>

      {/* Calendar Content */}
      <div className="flex flex-col gap-4">
          {/* Controls row: Filter + Navigation LEFT, Toggles RIGHT */}
          <div className="flex items-center justify-between gap-4 flex-wrap print:hidden">
            {/* LEFT: Filter icon + Date Navigation */}
            <div className="flex items-center gap-2">
              {/* Filter dropdown */}
              <Popover open={isFilterOpen} onOpenChange={setIsFilterOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    className={cn(
                      "h-10 w-10",
                      hasActiveFilters && "border-primary text-primary"
                    )}
                    aria-label={i18n._({ id: 'shifts.filter', message: 'Filter' })}
                  >
                    <Filter className="h-4 w-4" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-72" align="start">
                  <div className="space-y-4">
                    <h4 className="font-medium text-sm">
                      {i18n._({ id: 'shifts.filter.title', message: 'Filter shift view' })}
                    </h4>

                    {/* Childcare filter */}
                    <div className="space-y-2">
                      <label className="text-sm text-muted-foreground flex items-center gap-2">
                        <Building2 className="h-4 w-4" />
                        {i18n._({ id: 'shifts.filter.childcare', message: 'Childcare' })}
                      </label>
                      <Select
                        value={selectedChildcareId || '__all__'}
                        onValueChange={handleChildcareChange}
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder={i18n._({ id: 'shifts.filter.allChildcares', message: 'All childcares' })} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__all__">
                            {i18n._({ id: 'shifts.filter.allChildcares', message: 'All childcares' })}
                          </SelectItem>
                          {childcares.map((childcare) => (
                            /* A childcare row from the API always carries an id; the generated model marks it optional. */
                            <SelectItem key={childcare.id} value={childcare.id!}>
                              {childcare.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Group filter */}
                    <div className="space-y-2">
                      <label className="text-sm text-muted-foreground flex items-center gap-2">
                        <Users className="h-4 w-4" />
                        {i18n._({ id: 'shifts.filter.group', message: 'Group' })}
                      </label>
                      <Select
                        value={selectedGroupId || '__all__'}
                        onValueChange={handleGroupChange}
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder={i18n._({ id: 'shifts.filter.allGroups', message: 'All groups' })} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__all__">
                            {i18n._({ id: 'shifts.filter.allGroups', message: 'All groups' })}
                          </SelectItem>
                          {filteredGroupsForDropdown.map((group) => (
                            <SelectItem key={group.id} value={group.id || ''}>
                              {group.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Employee filter (#2038) */}
                    <div className="space-y-2">
                      <label
                        htmlFor="planner-employee-filter"
                        className="text-sm text-muted-foreground flex items-center gap-2"
                      >
                        <UserRound className="h-4 w-4" />
                        {i18n._({ id: 'shifts.filter.employee', message: 'Employee' })}
                      </label>
                      <EnhancedSingleSelect
                        id="planner-employee-filter"
                        value={filterEmployeeId ?? '__all__'}
                        onChange={(value) => setFilterEmployeeId(value === '__all__' ? null : value)}
                        options={[
                          {
                            value: '__all__',
                            label: i18n._({ id: 'shifts.filter.allEmployees', message: 'All employees' }),
                          },
                          ...employeeFilterOptions,
                        ]}
                      />
                    </div>
                  </div>
                </PopoverContent>
              </Popover>

              <Button
                variant="outline"
                size="icon"
                onClick={handlePrevious}
                className="h-10 w-10"
                aria-label={
                  calendarView === "day"
                    ? i18n._({
                        id: "shifts.previousDay",
                        message: "Previous day",
                      })
                    : calendarView === "month"
                      ? i18n._({
                          id: "shifts.previousMonth",
                          message: "Previous month",
                        })
                      : i18n._({
                          id: "shifts.previousWeek",
                          message: "Previous week",
                        })
                }
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>

              <div
                className={calendarView === "day" ? "w-full sm:w-[210px]" : "w-full sm:w-[360px]"}
              >
                {calendarView === "day" ? (
                  <div className="flex gap-2">
                    <div className="flex-1 min-w-0">
                      <DatePicker
                        value={currentDate}
                        onChange={handleDateSelect}
                        placeholder={i18n._({
                          id: "shifts.selectDate",
                          message: "Select date",
                        })}
                        showClearButton={false}
                      />
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      className="shrink-0 h-10 px-3"
                      onClick={handleToday}
                    >
                      <span className="text-sm font-medium">
                        {i18n._({ id: "shifts.today", message: "Today" })}
                      </span>
                    </Button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <PlannerRangePicker
                      label={getNavigationLabel()}
                      value={currentDate}
                      onChange={setCurrentDate}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      className="shrink-0 h-10 px-3"
                      onClick={handleToday}
                    >
                      <span className="text-sm font-medium">
                        {i18n._({ id: "shifts.today", message: "Today" })}
                      </span>
                    </Button>
                  </div>
                )}
              </div>

              <Button
                variant="outline"
                size="icon"
                onClick={handleNext}
                className="h-10 w-10"
                aria-label={
                  calendarView === "day"
                    ? i18n._({
                        id: "shifts.nextDay",
                        message: "Next day",
                      })
                    : calendarView === "month"
                      ? i18n._({
                          id: "shifts.nextMonth",
                          message: "Next month",
                        })
                      : i18n._({
                          id: "shifts.nextWeek",
                          message: "Next week",
                        })
                }
              >
                <ChevronRight className="h-4 w-4" />
              </Button>

              {/* Clear All Filters button - only shown when filters are active */}
              {hasActiveFilters && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleClearAllFilters}
                  className="text-muted-foreground hover:text-foreground"
                >
                  <X className="h-4 w-4 mr-1" />
                  <Trans id="shifts.filter.clearAll">Clear All Filters</Trans>
                </Button>
              )}
            </div>

            {/* RIGHT: View toggles */}
            <div className="flex items-center gap-4">
              {isGroupDayView && staffSavedToday !== null && (
                <span className="text-sm text-muted-foreground" data-testid="combination-savings">
                  {i18n._({
                    id: "shifts.combination.summary",
                    message: "{count, plural, one {Combinations today save # staff} other {Combinations today save # staff}}",
                    values: { count: staffSavedToday },
                  })}
                </span>
              )}
              <TooltipProvider delayDuration={300}>
              {/* Resource Mode Toggle (hidden in month view - always by employee) */}
              {calendarView !== "month" && (
                <div className="flex gap-1 p-1 bg-muted rounded-md">
                  <ExplainedButton
                    tip={i18n._({
                      id: "shifts.tip.byGroup",
                      message: "One row per group: who is in the group when, the cover mark, and the combined groups.",
                    })}
                  >
                    <Button
                      size="sm"
                      variant={viewMode === "byGroup" ? "default" : "ghost"}
                      onClick={() => setViewMode("byGroup")}
                      className="h-8"
                    >
                      {i18n._({ id: "shifts.byGroup", message: "By Group" })}
                    </Button>
                  </ExplainedButton>
                  <ExplainedButton
                    tip={i18n._({
                      id: "shifts.tip.byEmployee",
                      message: "One row per employee: their shifts and their working time account. Drag a shift to another row to hand it to a colleague.",
                    })}
                  >
                    <Button
                      size="sm"
                      variant={viewMode === "byEmployee" ? "default" : "ghost"}
                      onClick={() => setViewMode("byEmployee")}
                      className="h-8"
                    >
                      {i18n._({
                        id: "shifts.byEmployee",
                        message: "By Employee",
                      })}
                    </Button>
                  </ExplainedButton>
                </div>
              )}

              {/* Calendar View Toggle */}
              <div className="flex gap-1 p-1 bg-muted rounded-md">
                <ExplainedButton
                  tip={i18n._({
                    id: "shifts.tip.month",
                    message: "The whole month per employee with the planned hours.",
                  })}
                >
                  <Button
                    size="sm"
                    variant={calendarView === "month" ? "default" : "ghost"}
                    onClick={() => handleViewChange("month")}
                    className="h-8"
                  >
                    {i18n._({ id: "shifts.monthView", message: "Month" })}
                  </Button>
                </ExplainedButton>
                <ExplainedButton
                  tip={i18n._({
                    id: "shifts.tip.week",
                    message: "Seven days side by side. Drag a shift to another day to move it.",
                  })}
                >
                  <Button
                    size="sm"
                    variant={calendarView === "week" ? "default" : "ghost"}
                    onClick={() => handleViewChange("week")}
                    className="h-8"
                  >
                    {i18n._({ id: "shifts.weekView", message: "Week" })}
                  </Button>
                </ExplainedButton>
                <ExplainedButton
                  tip={i18n._({
                    id: "shifts.tip.day",
                    message: "One day on a timeline. By Group it shows the staffing per quarter hour and the expected arrivals of the children.",
                  })}
                >
                  <Button
                    size="sm"
                    variant={calendarView === "day" ? "default" : "ghost"}
                    onClick={() => handleViewChange("day")}
                    className="h-8"
                  >
                    {i18n._({ id: "shifts.dayView", message: "Day" })}
                  </Button>
                </ExplainedButton>
              </div>
              </TooltipProvider>
            </div>
          </div>

          {/* Active filters row */}
          {hasActiveFilters && (
            <div className="flex flex-wrap gap-2 print:hidden">
              {selectedChildcareName && (
                <div className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-primary/10 text-primary border border-primary/20 rounded-md">
                  <Building2 className="h-3 w-3" />
                  <span className="font-semibold">{i18n._({ id: 'shifts.filter.childcare', message: 'Childcare' })}:</span>
                  <span className="truncate max-w-32" title={selectedChildcareName}>
                    {selectedChildcareName}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setSelectedChildcareId(null);
                      setSelectedGroupId(null);
                    }}
                    className="h-4 w-4 p-0 hover:bg-destructive/20 hover:text-destructive ml-1"
                    aria-label={i18n._({ id: 'shifts.filter.removeChildcare', message: 'Remove childcare filter' })}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              )}
              {filterEmployeeName && (
                <div className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-primary/10 text-primary border border-primary/20 rounded-md">
                  <UserRound className="h-3 w-3" />
                  <span className="font-semibold">{i18n._({ id: 'shifts.filter.employee', message: 'Employee' })}:</span>
                  <span className="truncate max-w-32" title={filterEmployeeName}>
                    {filterEmployeeName}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setFilterEmployeeId(null)}
                    className="h-4 w-4 p-0 hover:bg-destructive/20 hover:text-destructive ml-1"
                    aria-label={i18n._({ id: 'shifts.filter.removeEmployee', message: 'Remove employee filter' })}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              )}
              {selectedGroupName && (
                <div className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-primary/10 text-primary border border-primary/20 rounded-md">
                  <Users className="h-3 w-3" />
                  <span className="font-semibold">{i18n._({ id: 'shifts.filter.group', message: 'Group' })}:</span>
                  <span className="truncate max-w-32" title={selectedGroupName}>
                    {selectedGroupName}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelectedGroupId(null)}
                    className="h-4 w-4 p-0 hover:bg-destructive/20 hover:text-destructive ml-1"
                    aria-label={i18n._({ id: 'shifts.filter.removeGroup', message: 'Remove group filter' })}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* The groups a group lead plans (#2043) */}
          {leadGroupIds && leadGroupIds.length > 0 && (
            <div className="mb-2 flex items-center gap-1 text-sm text-muted-foreground">
              <p data-testid="planner-lead-scope">
                {i18n._({
                  id: "shifts.leadScope.header",
                  message: "You plan for: {groups}",
                  values: { groups: leadGroupNames },
                })}
              </p>
              <HelpTip
                data-testid="planner-lead-scope-help"
                content={i18n._({
                  id: "shifts.leadScope.help",
                  message: "You can only plan shifts of the groups you lead. You can view other groups.",
                })}
              />
            </div>
          )}

          {/* Draft / published / confirmed counts of the visible range (#2038) */}
          <PlannerStatusStrip
            fromDate={fromDate}
            toDate={toDate}
            filtered={!!hasActiveFilters}
            onPublish={() => {
              setPublishDialogMode('publish');
              setPublishDialogOpen(true);
            }}
          />

          {/* Calendar Grid */}
          <div>
            {/* Loading State */}
            {isLoading && (
              <div className="space-y-4">
                <Skeleton className="h-[52px] w-full" />
                <Skeleton className="h-[80px] w-full" />
                <Skeleton className="h-[80px] w-full" />
                <Skeleton className="h-[80px] w-full" />
              </div>
            )}

            {/* Error State */}
            {isError && (
              <QueryErrorState
                error={error}
                onRetry={() => void refetchShifts()}
                title={i18n._({
                  id: "shifts.error",
                  message: "Error loading shifts",
                })}
              />
            )}

            {/* Calendar Grid */}
            {!isLoading && !isError && calendarView === "month" && (
              <ShiftMonthView
                shifts={visibleShifts}
                absences={visibleAbsences}
                currentDate={currentDate}
                employeeMap={visibleEmployeeMap}
                onShiftClick={handleShiftClick}
                onCellClick={handleCellClick}
                filterChildcareId={selectedChildcareId}
                filterGroupId={selectedGroupId}
              />
            )}
            {!isLoading && !isError && calendarView !== "month" && (
              <ShiftCalendarGrid
                shifts={visibleShifts}
                absences={visibleAbsences}
                viewMode={viewMode}
                calendarView={calendarView}
                currentDate={currentDate}
                employeeMap={visibleEmployeeMap}
                groupMap={groupMap}
                coverageData={viewMode === "byGroup" ? coverageData : undefined}
                slotCoverage={isGroupDayView ? slotCoverageData : undefined}
                combinations={viewMode === "byGroup" ? combinations : undefined}
                onCombinationClick={isAdmin ? handleCombinationClick : undefined}
                previewSuggestion={viewMode === "byGroup" && suggestionsOpen ? previewSuggestion : null}
                zeitkontoByEmployee={
                  isZeitkontoEnabled ? zeitkontoByEmployee : undefined
                }
                onShiftClick={handleShiftClick}
                onCellClick={handleCellClick}
                onAbsenceClick={handleAbsenceClick}
                filterChildcareId={selectedChildcareId}
                filterGroupId={selectedGroupId}
                onShiftDrop={shiftMove.onShiftDrop}
                canDragShift={writeScope.unrestricted ? undefined : canEditShift}
                availability={showsAvailability ? availabilityRange : undefined}
              />
            )}
          </div>
      </div>

      {/* Detail Dialog */}
      <ShiftDetailDialog
        shift={selectedShift}
        open={detailDialogOpen}
        onOpenChange={setDetailDialogOpen}
        onEdit={handleEditShift}
        onDelete={handleDeleteShift}
        onDeleteSeries={handleDeleteSeries}
        readOnly={!!selectedShift && !canEditShift(selectedShift)}
      />

      {/* Create Shift Dialog */}
      <CreateShiftDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        defaultDate={selectedDate || undefined}
        defaultEmployeeId={selectedEmployeeId}
      />

      {/* Group Combination Dialog */}
      <CombineGroupsDialog
        open={combineDialogOpen}
        onOpenChange={(open) => {
          setCombineDialogOpen(open);
          if (!open) {
            setSelectedCombination(null);
            setSuggestionPrefill(null);
          }
        }}
        groups={allGroups}
        combination={editingCombination}
        suggestion={suggestionPrefill?.suggestion ?? null}
        defaultValidFrom={suggestionPrefill?.validFrom ?? currentDate}
      />

      {/* Combination Suggestions Panel */}
      {isAdmin && (
        <CombinationSuggestionsPanel
          open={suggestionsOpen}
          onOpenChange={handleSuggestionsOpenChange}
          childcares={childcares}
          filterChildcareId={selectedChildcareId}
          preview={previewSuggestion}
          onCreate={handleCreateFromSuggestion}
          onShowOnDay={handleShowSuggestionOnDay}
        />
      )}

      {/* Edit Shift Dialog */}
      {selectedShift && (
        <EditShiftDialog
          shift={selectedShift}
          open={editDialogOpen}
          onOpenChange={setEditDialogOpen}
        />
      )}

      {/* Delete Shift Dialog */}
      {selectedShift && (
        <DeleteShiftDialog
          shift={selectedShift}
          open={deleteDialogOpen}
          onOpenChange={setDeleteDialogOpen}
        />
      )}

      {/* Delete Series Dialog - only render when pattern data is loaded to avoid showing 0 shifts */}
      {selectedShift?.pattern_id && patternData && (
        <DeleteSeriesDialog
          patternId={selectedShift.pattern_id}
          /* The pattern response always reports its shift_count; the generated model marks it optional. */
          shiftCount={patternData.shift_count!}
          selectedShiftDate={selectedShift.shift_date}
          open={deleteSeriesDialogOpen}
          onOpenChange={setDeleteSeriesDialogOpen}
        />
      )}

      {/* Edit Absence Dialog - reuses the same dialog from AbsenceManagementPage */}
      {selectedAbsence && (
        <EditAbsenceDialog
          absence={selectedAbsence}
          open={editAbsenceDialogOpen}
          onOpenChange={(open) => {
            setEditAbsenceDialogOpen(open);
            if (!open) setSelectedAbsence(null);
          }}
          onSuccess={() => {
            setEditAbsenceDialogOpen(false);
            setSelectedAbsence(null);
          }}
        />
      )}

      {/* Publish/Unpublish Week Dialog */}
      {/* With a group filter the publish and unpublish stay inside that group (#2038 UX pass). */}
      <PublishWeekDialog
        mode={publishDialogMode}
        fromDate={fromDate}
        toDate={toDate}
        open={publishDialogOpen}
        onOpenChange={setPublishDialogOpen}
        onShowRange={handleShowPublishedRange}
        groupIds={selectedGroupId ? [selectedGroupId] : undefined}
        groupName={selectedGroupName}
        employeeFiltered={!!filterEmployeeId}
        leadGroupIds={leadGroupIds}
        leadGroupNames={leadGroupNames}
      />

      {/* Drag and drop confirmations (#2038) */}
      {shiftMove.dialogs}

      {/* Copy Week Dialog */}
      <CopyWeekDialog
        open={copyWeekDialogOpen}
        onOpenChange={setCopyWeekDialogOpen}
        sourceWeekStart={weekStart}
      />
    </div>
    </ShiftRequestMarksProvider>
  );
}
